<template>
  <section class="home-statement">
    <DsText as="subtitle7" class="home-statement__title">Último estado de cuenta</DsText>

    <label class="home-statement__field">
      <span class="home-statement__label">Fecha de corte</span>
      <select v-model="contratoModel" class="home-statement__select">
        <option v-for="item in contratos" :key="item.contratoId" :value="item">
          {{ item.numeroContrato }}
        </option>
      </select>
      <img :src="arrowIcon" alt="" class="home-statement__arrow" aria-hidden="true">
    </label>

    <div class="home-statement__detail">
      <DsText as="body4" class="home-statement__detail-label">Fecha limite de pago</DsText>
      <DsText as="subtitle8" class="home-statement__detail-value">{{ fechaLimite || '—' }}</DsText>
    </div>

    <!-- Conservado del home actual (no está en el Figma) -->
    <div v-if="totalPagar != null" class="home-statement__detail">
      <DsText as="body4" class="home-statement__detail-label">Total a pagar</DsText>
      <DsText as="subtitle7" class="home-statement__detail-value">
        {{ filtros.filtroDivisa(totalPagar) }}
      </DsText>
    </div>

    <div v-if="referencia" class="home-statement__detail">
      <DsText as="body4" class="home-statement__detail-label">Referencia única</DsText>
      <DsText as="subtitle8" class="home-statement__detail-value">{{ referencia }}</DsText>
    </div>

    <a
      v-if="linkDescarga"
      class="home-statement__cta"
      :href="linkDescarga"
      target="_blank"
      rel="noreferrer noopener"
      @click="registrarDescarga"
    >
      <img :src="downloadIcon" alt="" class="home-statement__cta-icon" aria-hidden="true">
      Consultar
    </a>

    <hr class="home-statement__divider">

    <div class="home-statement__links">
      <HomeListItem
        title="Facturación"
        subtitle="Genera tu factura"
        :to="{ name: 'cliente-cliente-facturacion' }"
      />
      <HomeListItem
        title="Aclaraciones"
        subtitle="Reporta una incidencia"
        @click="emit('abrir-aclaraciones')"
      />
      <HomeListItem
        title="Domiciliar pago"
        subtitle="Activa o administra"
        :to="{ name: 'cliente-cliente-domiciliacion' }"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import filtros from '@/composables/filtros'
import { Cliente } from '@/servicios/api'
import DsText from '../MaterialApoyo/atoms/DsText.vue'
import HomeListItem from './HomeListItem.vue'

type Contrato = Cliente.Financiamiento.InformacionFinanciamientoContrato

const props = defineProps<{
  contratos: Contrato[]
  contratoSeleccionado?: Contrato
  fechaLimite?: string
  totalPagar?: number
  referencia?: string
  linkDescarga?: string
}>()

const emit = defineEmits<{
  (e: 'abrir-aclaraciones'): void
  (e: 'update:contratoSeleccionado', value: Contrato): void
}>()

const arrowIcon = new URL('../../assets/home/ic_arrow_drop_down.svg', import.meta.url).href
const downloadIcon = new URL('../../assets/home/ic_download.svg', import.meta.url).href

const contratoModel = computed({
  get: () => props.contratoSeleccionado,
  set: value => value && emit('update:contratoSeleccionado', value)
})

function registrarDescarga() {
  gtag('event', 'descargar', {
    event_category: 'complemento-estado-cuenta',
    event_label: 'home',
    value: 1
  })
}
</script>

<style scoped>
.home-statement {
  background: var(--ds-purple-900);
  border-radius: 16px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.home-statement__title {
  color: #f7f7f7;
  line-height: 24px;
}

.home-statement__field {
  position: relative;
  display: block;
}

.home-statement__label {
  position: absolute;
  top: -6px;
  left: 9px;
  padding: 0 4px;
  background: var(--ds-purple-900);
  color: #edeafc;
  font-size: 14px;
  line-height: 12px;
  letter-spacing: 0.15px;
}

.home-statement__select {
  width: 100%;
  height: 56px;
  padding: 8px 40px 8px 14px;
  border: 1px solid #f7f7fa;
  border-radius: var(--ds-radius-input);
  background: transparent;
  color: #edeafc;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.15px;
  appearance: none;
  cursor: pointer;
}

.home-statement__select:focus-visible {
  outline: 2px solid #edeafc;
  outline-offset: 1px;
}

.home-statement__select option {
  color: var(--ds-text-primary);
}

/*
  El SVG es de 10x5 con preserveAspectRatio="none": debe conservar su tamaño
  nativo (Figma lo centra en un área de 24x24). Escalarlo a 24x24 lo deforma.
*/
.home-statement__arrow {
  position: absolute;
  top: 50%;
  right: 21px;
  width: 10px;
  height: 5px;
  transform: translateY(-50%);
  pointer-events: none;
}

.home-statement__detail {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.home-statement__detail-label,
.home-statement__detail-value {
  color: #edeafc;
  line-height: 20px;
}

.home-statement__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  border-radius: 24px;
  background: #fbfbfb;
  color: var(--ds-purple-main);
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
}

.home-statement__cta-icon {
  width: 24px;
  height: 24px;
}

.home-statement__divider {
  height: 1px;
  margin: 0;
  border: none;
  background: #ebeaf3;
}

.home-statement__links {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
